import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  myAlliance,
  listPublicAlliances,
  allianceInfo,
  createAlliance,
  joinAlliance,
  leaveAlliance,
  editAlliance,
  approveMember,
  rejectMember,
  promoteMember,
  demoteMember,
  kickMember,
  transferChief,
  patchAllianceSettings,
  listAllianceHospitals,
  placeAllianceHospital,
  buyAllianceBed,
  buyAllianceSpecialty,
  deleteAllianceHospital,
  setDonationPct,
  setHospitalFeePct,
  formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import {
  UsersThree,
  MagnifyingGlass,
  Lock,
  Globe,
  Star,
  SignOut,
  PencilSimple,
  Check,
  X,
  CaretLeft,
  ChatCircle,
  Bank,
  Buildings,
  Plus,
  Trash,
  MapPinLine,
} from "@phosphor-icons/react";
import PremiumBadge from "@/components/PremiumBadge";
import AllianceChat from "@/components/AllianceChat";
import AllianceBuildingsMapDialog from "@/components/AllianceBuildingsMapDialog";

// Window for "X ago" formatting (used in last-seen tooltip / line for staff).
const fmtAgo = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
};

// Rank icon: chief = gold star, deputy = white star, member = nothing.
function RankIcon({ rank, size = 11 }) {
  if (rank === "chief") {
    return (
      <Star
        size={size}
        weight="fill"
        className="text-amber-400 shrink-0"
        title="Chief"
        aria-label="Chief"
        data-testid="rank-chief"
      />
    );
  }
  if (rank === "deputy") {
    return (
      <Star
        size={size}
        weight="fill"
        className="text-white shrink-0"
        title="Deputy Chief"
        aria-label="Deputy Chief"
        data-testid="rank-deputy"
      />
    );
  }
  return null;
}

// Friendly rank label for tooltips / text strings.
const RANK_LABEL = { chief: "Chief", deputy: "Deputy Chief", member: "Member" };

export default function MutualAidDialog({ open, onOpenChange, currentUser, playerDonationPct = 0, playerHospitalFeePct = 0, onAllianceChanged, onPlayerSettingsChanged, onOpenAllianceHospitalPlacement }) {
  // Tabs: overview | members | chat | search  (search is always rightmost).
  // Default depends on whether the user is in a group.
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({ alliance: null, pending_applications: [] });
  const [createForm, setCreateForm] = useState({ name: "", bio: "", public: true });
  // Edit form is hydrated from the alliance and toggled via the pencil icon.
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", bio: "", public: true });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await myAlliance();
      setState(d);
      if (d.alliance) {
        setEditForm({
          name: d.alliance.name,
          bio: d.alliance.bio || "",
          public: d.alliance.public,
        });
      }
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If the player is in a group default to Overview; otherwise jump to Search
  // so they can immediately browse / create.
  useEffect(() => {
    if (!open) return;
    if (state.alliance) {
      if (!["overview", "members", "chat", "search"].includes(tab)) {
        setTab("overview");
      }
    } else {
      if (tab !== "search") setTab("search");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, state.alliance?.id]);

  const inAlliance = !!state.alliance;
  const viewerRank = state.alliance?.viewer_rank || "member";
  const isStaff = viewerRank === "chief" || viewerRank === "deputy";
  const isChief = viewerRank === "chief";

  // ---------- Actions ----------
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await createAlliance({
        name: createForm.name.trim(),
        bio: createForm.bio.trim(),
        public: !!createForm.public,
      });
      toast.success(`Created "${res.name}"`);
      setCreateForm({ name: "", bio: "", public: true });
      await refresh();
      onAllianceChanged && onAllianceChanged();
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail) || "Could not create group");
    }
  };

  const handleLeave = async () => {
    if (!state.alliance) return;
    if (isChief && state.alliance.member_count > 1) {
      toast.error("Transfer the Chief role first — use the crown button next to a member.");
      return;
    }
    const msg = state.alliance.member_count <= 1
      ? "You're the last member — the group will disband. Continue?"
      : "Leave this group?";
    if (!window.confirm(msg)) return;
    try {
      await leaveAlliance(state.alliance.id);
      toast.success("Left group");
      await refresh();
      onAllianceChanged && onAllianceChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Leave failed");
    }
  };

  const handleSaveEdit = async () => {
    try {
      await editAlliance(state.alliance.id, {
        name: editForm.name.trim(),
        bio: editForm.bio.trim(),
        public: !!editForm.public,
      });
      toast.success("Group updated");
      setEditing(false);
      await refresh();
      onAllianceChanged && onAllianceChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    }
  };

  const memberAction = async (fn, ok, uid) => {
    try {
      await fn(state.alliance.id, uid);
      toast.success(ok);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Action failed");
    }
  };

  const handleApprove = (uid) => memberAction(approveMember, "Approved", uid);
  const handleReject = (uid) => memberAction(rejectMember, "Rejected", uid);
  const handlePromote = (uid) => memberAction(promoteMember, "Promoted to Deputy Chief", uid);
  const handleDemote = (uid) => memberAction(demoteMember, "Demoted to Member", uid);
  const handleKick = (uid, name) => {
    if (!window.confirm(`Kick "${name}" out of the group?`)) return;
    return memberAction(kickMember, `${name} kicked`, uid);
  };
  const handleTransfer = (uid, name) => {
    if (!window.confirm(`Transfer the Chief role to ${name}? You will become a regular Member.`)) return;
    return memberAction(transferChief, `${name} is now Chief`, uid);
  };

  // Tabs configuration. Order: Overview, Members, Chat, Facilities, Search.
  const tabs = useMemo(() => {
    const out = [];
    if (inAlliance) {
      out.push({ id: "overview", label: "Overview" });
      out.push({ id: "members", label: "Members" });
      out.push({ id: "chat", label: "Chat" });
      out.push({ id: "facilities", label: "Facilities" });
    }
    out.push({ id: "search", label: "Search" });
    return out;
  }, [inAlliance]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="mutual-aid-dialog"
        className="rounded-none border border-white/10 bg-[#0F1115] text-white max-w-2xl max-h-[88vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="mono text-[12px] uppercase tracking-[0.25em] text-zinc-300 flex items-center gap-2">
            <UsersThree size={16} /> Mutual Aid Groups
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b border-white/10 pb-2" data-testid="mutual-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`mutual-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 border ${
                tab === t.id
                  ? "border-emerald-400 text-emerald-400 bg-emerald-400/10"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest text-center py-4">Loading…</div>
        )}

        {!loading && tab === "overview" && inAlliance && (
          <OverviewTab
            alliance={state.alliance}
            isStaff={isStaff}
            editing={editing}
            setEditing={setEditing}
            editForm={editForm}
            setEditForm={setEditForm}
            onSaveEdit={handleSaveEdit}
            onLeave={handleLeave}
            isChief={isChief}
            playerDonationPct={playerDonationPct}
            playerHospitalFeePct={playerHospitalFeePct}
            onPlayerSettingsChanged={onPlayerSettingsChanged}
            onAllianceChanged={onAllianceChanged}
            refresh={refresh}
          />
        )}

        {!loading && tab === "members" && inAlliance && (
          <MembersTab
            alliance={state.alliance}
            currentUser={currentUser}
            viewerRank={viewerRank}
            onApprove={handleApprove}
            onReject={handleReject}
            onPromote={handlePromote}
            onDemote={handleDemote}
            onKick={handleKick}
            onTransfer={handleTransfer}
          />
        )}

        {!loading && tab === "chat" && inAlliance && (
          <div
            className="pt-2 h-[60vh] max-h-[60vh] flex flex-col"
            data-testid="mutual-chat-tab"
          >
            <AllianceChat allianceId={state.alliance.id} currentUserId={currentUser?.user_id} />
          </div>
        )}

        {!loading && tab === "facilities" && inAlliance && (
          <FacilitiesTab
            alliance={state.alliance}
            isStaff={isStaff}
            onAllianceChanged={onAllianceChanged}
            refresh={refresh}
            onOpenAllianceHospitalPlacement={onOpenAllianceHospitalPlacement}
          />
        )}

        {!loading && tab === "search" && (
          <SearchTab
            inAlliance={inAlliance}
            createForm={createForm}
            setCreateForm={setCreateForm}
            onCreate={handleCreate}
            onJoinChanged={async () => {
              await refresh();
              onAllianceChanged && onAllianceChanged();
            }}
            pendingApplications={state.pending_applications}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- OVERVIEW TAB ----------
function OverviewTab({ alliance, isStaff, editing, setEditing, editForm, setEditForm, onSaveEdit, onLeave, isChief, playerDonationPct, playerHospitalFeePct, onAllianceChanged, onPlayerSettingsChanged, refresh }) {
  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-3" data-testid="overview-tab">
      <div className="border border-white/10 p-3">
        {!editing ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              {alliance.public ? <Globe size={14} className="text-emerald-400" /> : <Lock size={14} className="text-amber-400" />}
              <h3 className="text-lg font-bold tracking-tight flex-1">{alliance.name}</h3>
              {isStaff && (
                <button
                  type="button"
                  data-testid="overview-edit-btn"
                  onClick={() => setEditing(true)}
                  title={`Edit group (${RANK_LABEL[isChief ? "chief" : "deputy"]} only)`}
                  className="text-zinc-500 hover:text-emerald-400 p-1"
                >
                  <PencilSimple size={14} />
                </button>
              )}
              <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">
                {alliance.member_count}/{alliance.max_members}
              </span>
            </div>
            <div className="mono text-[10px] text-zinc-400 uppercase tracking-wider mb-2">
              {alliance.public ? "Public — anyone can join" : "Private — invite-only"}
            </div>
            <div className="text-[12px] text-zinc-300 leading-relaxed whitespace-pre-wrap">
              {alliance.bio || <span className="text-zinc-600 italic">No description</span>}
            </div>
          </>
        ) : (
          <div className="space-y-2" data-testid="overview-edit-form">
            <Input
              data-testid="overview-edit-name"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Group name"
              maxLength={40}
              className="rounded-none border-emerald-400/40 bg-black/40 mono tracking-wider focus-visible:ring-emerald-500/30"
            />
            <textarea
              data-testid="overview-edit-bio"
              value={editForm.bio}
              onChange={(e) => setEditForm((f) => ({ ...f, bio: e.target.value }))}
              placeholder="Description"
              maxLength={400}
              rows={3}
              className="w-full rounded-none border border-emerald-400/40 bg-black/40 mono tracking-wider text-sm p-2 focus-visible:ring-emerald-500/30"
            />
            <label className="flex items-center gap-2 text-[11px] mono uppercase tracking-widest text-zinc-400">
              <input
                type="checkbox"
                data-testid="overview-edit-public"
                checked={editForm.public}
                onChange={(e) => setEditForm((f) => ({ ...f, public: e.target.checked }))}
              />
              Public (anyone can join)
            </label>
            <div className="flex gap-2 pt-1">
              <Button
                data-testid="overview-save-btn"
                onClick={onSaveEdit}
                size="sm"
                className="rounded-none h-7 mono uppercase text-[10px] tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black"
              >
                <Check size={12} className="mr-1" /> Save
              </Button>
              <Button
                data-testid="overview-cancel-btn"
                onClick={() => setEditing(false)}
                variant="outline"
                size="sm"
                className="rounded-none h-7 mono uppercase text-[10px] tracking-widest border-white/15"
              >
                <X size={12} className="mr-1" /> Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Group stats */}
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Members" value={`${alliance.member_count} / ${alliance.max_members}`} />
        <Stat label="Visibility" value={alliance.public ? "PUBLIC" : "PRIVATE"} />
        <Stat label="Bank" value={`$${(alliance.bank || 0).toLocaleString()}`} />
      </div>

      <EconomyBlock
        alliance={alliance}
        isChief={isChief}
        playerDonationPct={playerDonationPct}
        playerHospitalFeePct={playerHospitalFeePct}
        onPlayerSettingsChanged={onPlayerSettingsChanged}
        onAllianceChanged={onAllianceChanged}
        refresh={refresh}
      />

      <Button
        data-testid="leave-btn"
        onClick={onLeave}
        variant="outline"
        className="rounded-none border-red-500/40 text-red-400 hover:bg-red-500/10 mono uppercase text-[10px] tracking-widest"
      >
        <SignOut size={12} className="mr-1.5" /> Leave Group
      </Button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 px-3 py-2">
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-[14px] font-bold mono tracking-wider">{value}</div>
    </div>
  );
}

// ---------- MEMBERS TAB ----------
function MembersTab({
  alliance,
  currentUser,
  viewerRank,
  onApprove,
  onReject,
  onPromote,
  onDemote,
  onKick,
  onTransfer,
}) {
  const isStaff = viewerRank === "chief" || viewerRank === "deputy";
  const isChief = viewerRank === "chief";

  // Sort members by rank (chief > deputy > member) then name
  const RANK_RANK = { chief: 0, deputy: 1, member: 2 };
  const sorted = useMemo(() => {
    return [...(alliance.members || [])].sort((a, b) => {
      const ra = RANK_RANK[a.rank] ?? 9;
      const rb = RANK_RANK[b.rank] ?? 9;
      if (ra !== rb) return ra - rb;
      return (a.name || "").localeCompare(b.name || "");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alliance.members]);

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-3" data-testid="members-tab">
      {/* Pending applications (visible to staff) */}
      {isStaff && alliance.pending && alliance.pending.length > 0 && (
        <div className="border border-amber-500/30 p-3" data-testid="pending-list">
          <div className="mono text-[10px] uppercase tracking-widest text-amber-400 mb-2">
            Pending Applications ({alliance.pending.length})
          </div>
          <div className="space-y-1.5">
            {alliance.pending.map((p) => (
              <div key={p.user_id} className="flex items-center gap-2 text-[12px]">
                {p.is_premium && <PremiumBadge size={11} />}
                <span className="flex-1 truncate">{p.name}</span>
                <Button
                  data-testid={`approve-${p.user_id}`}
                  onClick={() => onApprove(p.user_id)}
                  size="sm"
                  className="rounded-none h-7 mono uppercase text-[9px] tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black"
                >
                  Approve
                </Button>
                <Button
                  data-testid={`reject-${p.user_id}`}
                  onClick={() => onReject(p.user_id)}
                  size="sm"
                  variant="outline"
                  className="rounded-none h-7 mono uppercase text-[9px] tracking-widest border-white/15"
                >
                  Reject
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-white/10 p-3">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center justify-between">
          <span>Members ({alliance.member_count})</span>
          <span className="text-[9px] text-zinc-600 normal-case tracking-normal">
            <Star size={9} weight="fill" className="inline text-amber-400 mr-1" />Chief ·{" "}
            <Star size={9} weight="fill" className="inline text-white mr-1" />Deputy Chief
          </span>
        </div>
        <div className="space-y-1.5">
          {sorted.map((m) => (
            <MemberRow
              key={m.user_id}
              member={m}
              isMe={m.user_id === currentUser?.user_id}
              viewerRank={viewerRank}
              isChiefViewer={isChief}
              isStaffViewer={isStaff}
              onPromote={onPromote}
              onDemote={onDemote}
              onKick={onKick}
              onTransfer={onTransfer}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function MemberRow({
  member,
  isMe,
  viewerRank,
  isChiefViewer,
  isStaffViewer,
  onPromote,
  onDemote,
  onKick,
  onTransfer,
}) {
  const rank = member.rank || "member";
  // Action permission rules
  // - Chief can promote any member, demote any deputy, kick any non-chief, transfer chief
  // - Deputy can kick non-chief AND non-deputy members
  // - Nobody can act on themselves via row actions (use leave / settings)
  const canPromote = isChiefViewer && rank === "member" && !isMe;
  const canDemote = isChiefViewer && rank === "deputy" && !isMe;
  const canTransfer = isChiefViewer && rank !== "chief" && !isMe;
  const canKick =
    !isMe &&
    rank !== "chief" &&
    (viewerRank === "chief" || (viewerRank === "deputy" && rank === "member"));

  return (
    <div
      className="flex items-center gap-2 text-[12px] py-0.5"
      data-testid={`member-${member.user_id}`}
    >
      {/* Online status dot */}
      <span
        title={member.online ? "Online" : "Offline"}
        data-testid={`presence-${member.user_id}`}
        className={`inline-block w-2 h-2 rounded-full shrink-0 ${
          member.online ? "bg-emerald-400" : "bg-zinc-600"
        }`}
      />
      <RankIcon rank={rank} />
      {member.is_premium && <PremiumBadge size={11} />}
      <span className="flex-1 truncate" title={member.name}>
        {member.name}
      </span>
      {isMe && <span className="mono text-[9px] uppercase text-zinc-500">You</span>}
      {/* Staff-only last-seen text — only shown when server included it */}
      {isStaffViewer && !member.online && member.last_seen_at && (
        <span
          className="mono text-[9px] uppercase tracking-widest text-zinc-500"
          title={`Last seen ${new Date(member.last_seen_at).toLocaleString()}`}
          data-testid={`last-seen-${member.user_id}`}
        >
          {fmtAgo(member.last_seen_at)}
        </span>
      )}
      {/* Actions */}
      <span className="inline-flex items-center gap-1 shrink-0">
        {canPromote && (
          <button
            type="button"
            data-testid={`promote-${member.user_id}`}
            onClick={() => onPromote(member.user_id)}
            title="Promote to Deputy Chief"
            className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-white/30 text-zinc-200 hover:border-white hover:bg-white/10"
          >
            ★ Promote
          </button>
        )}
        {canDemote && (
          <button
            type="button"
            data-testid={`demote-${member.user_id}`}
            onClick={() => onDemote(member.user_id)}
            title="Demote to Member"
            className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-500/50 text-zinc-300 hover:border-zinc-300 hover:bg-white/5"
          >
            Demote
          </button>
        )}
        {canTransfer && (
          <button
            type="button"
            data-testid={`transfer-chief-${member.user_id}`}
            onClick={() => onTransfer(member.user_id, member.name)}
            title="Transfer Chief role to this member"
            className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/60 text-amber-300 hover:bg-amber-500/10"
          >
            ★ Make Chief
          </button>
        )}
        {canKick && (
          <button
            type="button"
            data-testid={`kick-${member.user_id}`}
            onClick={() => onKick(member.user_id, member.name)}
            title="Kick member out"
            className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-red-500/60 text-red-300 hover:bg-red-500/10"
          >
            Kick
          </button>
        )}
      </span>
    </div>
  );
}

// ---------- SEARCH TAB ----------
function SearchTab({ inAlliance, createForm, setCreateForm, onCreate, onJoinChanged, pendingApplications }) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("members"); // members|free|created
  const [visibility, setVisibility] = useState("all"); // all|public|private
  const [list, setList] = useState([]);
  const [preview, setPreview] = useState(null); // alliance object being previewed
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, visibility };
      if (query.trim()) params.q = query.trim();
      const d = await listPublicAlliances(params);
      setList(d.alliances || []);
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  }, [query, sort, visibility]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleJoin = async (aid) => {
    try {
      const res = await joinAlliance(aid);
      if (res.joined) toast.success("Joined group");
      else if (res.requested) toast.info(res.already_pending ? "Already pending approval" : "Request sent");
      setPreview(null);
      await onJoinChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Join failed");
    }
  };

  if (preview) {
    return (
      <PreviewPanel
        preview={preview}
        inAlliance={inAlliance}
        onBack={() => setPreview(null)}
        onJoin={handleJoin}
      />
    );
  }

  return (
    <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-3" data-testid="search-tab">
      {!inAlliance && pendingApplications && pendingApplications.length > 0 && (
        <div className="border border-amber-500/30 p-2">
          <div className="mono text-[9px] uppercase tracking-widest text-amber-400 mb-1">Awaiting approval</div>
          {pendingApplications.map((p) => (
            <div key={p.id} className="text-[11px] flex items-center gap-2">
              <Lock size={10} /> {p.name}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          data-testid="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search groups by name…"
          className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-emerald-500/30"
        />
        <Button
          data-testid="search-btn"
          onClick={fetchList}
          className="rounded-none mono uppercase text-[10px] tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black"
        >
          <MagnifyingGlass size={12} />
        </Button>
      </div>

      {/* Sort + visibility toggles */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">Sort</span>
        <ToggleGroup
          value={sort}
          onChange={setSort}
          options={[
            { id: "members", label: "Most Members" },
            { id: "free", label: "Most Free Spots" },
            { id: "created", label: "Newest" },
          ]}
          prefix="search-sort"
        />
        <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 ml-2">Show</span>
        <ToggleGroup
          value={visibility}
          onChange={setVisibility}
          options={[
            { id: "all", label: "All" },
            { id: "public", label: "Public" },
            { id: "private", label: "Private" },
          ]}
          prefix="search-vis"
        />
      </div>

      {!inAlliance && (
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="toggle-create-form"
            onClick={() => setShowCreate((v) => !v)}
            className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-emerald-400/60 text-emerald-300 hover:bg-emerald-400/10"
          >
            {showCreate ? "× Cancel Create" : "+ Create Group"}
          </button>
        </div>
      )}

      {!inAlliance && showCreate && (
        <CreateForm form={createForm} setForm={setCreateForm} onSubmit={onCreate} />
      )}

      {loading && (
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest text-center py-4">Searching…</div>
      )}

      {!loading && list.length === 0 && (
        <div className="mono text-[10px] text-zinc-600 uppercase tracking-widest text-center py-4">
          — no groups match —
        </div>
      )}

      {!loading && list.map((a) => (
        <SearchRow
          key={a.id}
          a={a}
          inAlliance={inAlliance}
          onPreview={async () => {
            try {
              const fresh = await allianceInfo(a.id);
              setPreview(fresh);
            } catch (_e) {
              setPreview(a);
            }
          }}
        />
      ))}
    </div>
  );
}

function ToggleGroup({ value, onChange, options, prefix }) {
  return (
    <div className="inline-flex gap-1">
      {options.map((o) => (
        <button
          key={o.id}
          data-testid={`${prefix}-${o.id}`}
          onClick={() => onChange(o.id)}
          className={`mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${
            value === o.id
              ? "border-emerald-400 text-emerald-400 bg-emerald-400/10"
              : "border-white/10 text-zinc-400 hover:text-white"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SearchRow({ a, inAlliance, onPreview }) {
  return (
    <div
      data-testid={`alliance-row-${a.id}`}
      className="border border-white/10 p-3 flex items-start gap-3 hover:bg-white/5 cursor-pointer"
      onClick={onPreview}
    >
      {a.public ? (
        <Globe size={14} className="text-emerald-400 mt-0.5 shrink-0" />
      ) : (
        <Lock size={14} className="text-amber-400 mt-0.5 shrink-0" title="Invite-only" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold truncate flex items-center gap-2">
          {a.name}
          {!a.public && (
            <span className="mono text-[8px] uppercase tracking-widest border border-amber-500/40 text-amber-300 px-1 py-px">
              Invite-only
            </span>
          )}
        </div>
        <div className="text-[11px] text-zinc-400 line-clamp-2">
          {a.bio || <span className="text-zinc-600 italic">No description</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
          {a.member_count}/{a.max_members}
        </div>
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-600">
          {a.free_spots} open
        </div>
        <button
          type="button"
          data-testid={`view-${a.id}`}
          className="mt-1 mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-white/15 text-zinc-300 hover:border-white hover:bg-white/5"
        >
          View
        </button>
      </div>
    </div>
  );
}

function PreviewPanel({ preview, inAlliance, onBack, onJoin }) {
  const isFull = preview.member_count >= preview.max_members;
  const [buildingsOpen, setBuildingsOpen] = useState(false);
  const members = Array.isArray(preview.members) ? preview.members : [];
  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-3" data-testid="preview-panel">
      <button
        type="button"
        data-testid="preview-back-btn"
        onClick={onBack}
        className="mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1"
      >
        <CaretLeft size={12} /> Back to results
      </button>
      <div className="border border-white/10 p-3">
        <div className="flex items-center gap-2 mb-2">
          {preview.public ? (
            <Globe size={14} className="text-emerald-400" />
          ) : (
            <Lock size={14} className="text-amber-400" />
          )}
          <h3 className="text-lg font-bold tracking-tight flex-1">{preview.name}</h3>
          <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">
            {preview.member_count}/{preview.max_members}
          </span>
        </div>
        <div className="mono text-[10px] text-zinc-400 uppercase tracking-wider mb-2">
          {preview.public ? "Public — anyone can join" : "Private — invite-only"}
        </div>
        <div className="text-[12px] text-zinc-300 leading-relaxed whitespace-pre-wrap">
          {preview.bio || <span className="text-zinc-600 italic">No description</span>}
        </div>
      </div>

      {/* ---- Member roster (visible for public AND private groups) ---- */}
      <div className="border border-white/10 p-3" data-testid="preview-members">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center justify-between">
          <span>Members ({members.length})</span>
          <span className="text-[9px] text-zinc-600 normal-case tracking-normal">
            <Star size={9} weight="fill" className="inline text-amber-400 mr-1" />Chief ·{" "}
            <Star size={9} weight="fill" className="inline text-white mr-1" />Deputy Chief
          </span>
        </div>
        {members.length === 0 ? (
          <div className="text-[11px] text-zinc-600 italic mono uppercase tracking-widest">No members</div>
        ) : (
          <div className="space-y-1">
            {members.map((m) => (
              <div
                key={m.user_id}
                data-testid={`preview-member-${m.user_id}`}
                className="flex items-center gap-2 text-[12px] py-0.5"
              >
                {m.is_premium && <PremiumBadge size={10} />}
                <RankIcon rank={m.rank} size={10} />
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${
                    m.online ? "bg-emerald-400" : "bg-zinc-700"
                  }`}
                  title={m.online ? "Online" : `Last seen ${fmtAgo(m.last_seen_at)}`}
                />
                <span className="flex-1 truncate">{m.name || "—"}</span>
                <span className="mono text-[9px] uppercase tracking-widest text-zinc-600">
                  {RANK_LABEL[m.rank] || "Member"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button
          data-testid="preview-buildings-btn"
          onClick={() => setBuildingsOpen(true)}
          variant="outline"
          className="rounded-none mono uppercase text-[10px] tracking-widest border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10"
        >
          <MapPinLine size={12} weight="fill" className="mr-1.5" /> View All Buildings
        </Button>
        <Button
          data-testid="preview-join-btn"
          onClick={() => onJoin(preview.id)}
          disabled={inAlliance || isFull || !preview.public}
          className="rounded-none mono uppercase text-[10px] tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black disabled:opacity-40"
          title={
            inAlliance
              ? "Leave your current group first"
              : isFull
              ? "Group is full"
              : !preview.public
              ? "Invite-only — request must come from a member"
              : "Join this group"
          }
        >
          {!preview.public ? "Invite-only" : isFull ? "Full" : "Join"}
        </Button>
      </div>

      <AllianceBuildingsMapDialog
        open={buildingsOpen}
        onOpenChange={setBuildingsOpen}
        alliance={{ id: preview.id, name: preview.name }}
      />
    </div>
  );
}

function CreateForm({ form, setForm, onSubmit }) {
  return (
    <form
      onSubmit={onSubmit}
      className="border border-emerald-400/30 p-3 space-y-2"
      data-testid="create-form"
    >
      <div className="mono text-[10px] uppercase tracking-widest text-emerald-300">
        <ChatCircle size={11} className="inline mr-1" /> Create your own group
      </div>
      <Input
        data-testid="create-name-input"
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        placeholder="Group name (2–40 chars)"
        required
        maxLength={40}
        minLength={2}
        className="rounded-none border-white/10 bg-black/40 mono tracking-wider focus-visible:ring-emerald-500/30"
      />
      <textarea
        data-testid="create-bio-input"
        value={form.bio}
        onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
        placeholder="Description (optional)"
        maxLength={400}
        rows={3}
        className="w-full rounded-none border border-white/10 bg-black/40 mono tracking-wider text-sm p-2 focus-visible:ring-emerald-500/30"
      />
      <label className="flex items-center gap-2 text-[11px] mono uppercase tracking-widest text-zinc-400">
        <input
          data-testid="create-public-toggle"
          type="checkbox"
          checked={form.public}
          onChange={(e) => setForm((f) => ({ ...f, public: e.target.checked }))}
        />
        Public (anyone can join without approval)
      </label>
      <Button
        data-testid="create-submit-btn"
        type="submit"
        className="rounded-none mono uppercase text-[10px] tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black"
      >
        Create Group
      </Button>
    </form>
  );
}


// ---------- Economy block (donation % + hospital-fee % settings) ----------
function EconomyBlock({ alliance, isChief, playerDonationPct, playerHospitalFeePct, onAllianceChanged, onPlayerSettingsChanged, refresh }) {
  // Chief edits alliance-wide settings; everyone (including chief) sets their own
  // donation %. Alliance min acts as a floor for personal donations.
  const PCTS = [0, 5, 10];
  const minFloor = alliance.min_donation_pct || 0;
  const allianceFee = alliance.hospital_fee_pct || 0;
  // Personal donation cannot go below the alliance floor.
  const effectiveDonation = Math.max(playerDonationPct || 0, minFloor);

  const setAllianceMinDonation = async (pct) => {
    try { await patchAllianceSettings(alliance.id, { min_donation_pct: pct }); toast.success(`Alliance min donation set to ${pct}%`); await refresh(); onAllianceChanged && onAllianceChanged(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
  };
  const setAllianceFee = async (pct) => {
    try { await patchAllianceSettings(alliance.id, { hospital_fee_pct: pct }); toast.success(`Alliance hospital fee set to ${pct}%`); await refresh(); onAllianceChanged && onAllianceChanged(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
  };
  const setMyDonation = async (pct) => {
    try {
      await setDonationPct(pct);
      onPlayerSettingsChanged && onPlayerSettingsChanged({ donation_pct: pct });
      toast.success(`Your donation set to ${pct}%`);
      onAllianceChanged && onAllianceChanged();
    }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
  };
  const setMyHospitalFee = async (pct) => {
    try {
      await setHospitalFeePct(pct);
      onPlayerSettingsChanged && onPlayerSettingsChanged({ hospital_fee_pct: pct });
      toast.success(`Your hospital fee set to ${pct}%`);
      onAllianceChanged && onAllianceChanged();
    }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
  };

  return (
    <div className="border border-white/10 p-3 space-y-3" data-testid="economy-block">
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
        <Bank size={11} /> Economy
      </div>

      {/* Player's donation % */}
      <PctRow
        label="My donation per call"
        helpText={minFloor > 0 ? `Alliance min: ${minFloor}%` : "0% = keep everything"}
        value={effectiveDonation}
        options={PCTS.filter((p) => p >= minFloor)}
        onChange={setMyDonation}
        testidPrefix="my-donation"
      />

      {/* Player's hospital fee % */}
      <PctRow
        label="My hospital usage fee"
        helpText="Charged to alliance members who transport to your hospitals"
        value={playerHospitalFeePct || 0}
        options={PCTS}
        onChange={setMyHospitalFee}
        testidPrefix="my-hospital-fee"
      />

      {/* Chief-only alliance-wide settings */}
      {isChief && (
        <>
          <div className="border-t border-white/5 mt-2 pt-3 mono text-[9px] uppercase tracking-widest text-amber-300 flex items-center gap-1.5">
            <Star size={10} weight="fill" /> Chief Settings
          </div>
          <PctRow
            label="Min donation (alliance-wide)"
            helpText="Forces every member to donate at least this %"
            value={minFloor}
            options={PCTS}
            onChange={setAllianceMinDonation}
            testidPrefix="min-donation"
          />
          <PctRow
            label="Fee on alliance hospitals"
            helpText="Charged to anyone transporting to alliance-owned hospitals"
            value={allianceFee}
            options={PCTS}
            onChange={setAllianceFee}
            testidPrefix="alliance-fee"
          />
        </>
      )}
    </div>
  );
}

function PctRow({ label, helpText, value, options, onChange, testidPrefix }) {
  return (
    <div className="flex items-center justify-between gap-3" data-testid={`${testidPrefix}-row`}>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold tracking-tight">{label}</div>
        {helpText && <div className="mono text-[8px] uppercase tracking-widest text-zinc-500 mt-0.5">{helpText}</div>}
      </div>
      <div className="inline-flex gap-1">
        {options.map((p) => (
          <button
            key={p}
            type="button"
            data-testid={`${testidPrefix}-${p}`}
            onClick={() => p !== value && onChange(p)}
            className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
              value === p
                ? "border-emerald-400 text-emerald-400 bg-emerald-400/10"
                : "border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            {p}%
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- FACILITIES TAB ----------
function FacilitiesTab({ alliance, isStaff, onAllianceChanged, refresh, onOpenAllianceHospitalPlacement }) {
  const [data, setData] = useState({ alliance: [], members: [] });
  const [loading, setLoading] = useState(false);
  const [bedConfirmId, setBedConfirmId] = useState(null); // hid we're confirming bed buy
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // hid we're confirming demolish

  const load = async () => {
    setLoading(true);
    try {
      const d = await listAllianceHospitals(alliance.id);
      setData(d);
    } catch (_e) {/* */}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [alliance.id, refresh]);

  const handleBuyBed = async (hid) => {
    try { const r = await buyAllianceBed(alliance.id, hid); toast.success(`Bed added (-$${r.cost.toLocaleString()})`); await load(); await refresh(); onAllianceChanged && onAllianceChanged(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Bed purchase failed"); }
    finally { setBedConfirmId(null); }
  };
  const handleBuySpecialty = async (hid, spec) => {
    try { await buyAllianceSpecialty(alliance.id, hid, spec); toast.success(`${spec} unlocked`); await load(); await refresh(); onAllianceChanged && onAllianceChanged(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Specialty purchase failed"); }
  };
  const handleDelete = async (hid) => {
    try {
      const r = await deleteAllianceHospital(alliance.id, hid);
      toast.success(`${r.name || "Hospital"} demolished`);
      await load();
      await refresh();
      onAllianceChanged && onAllianceChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed"); }
    finally { setDeleteConfirmId(null); }
  };

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-3" data-testid="facilities-tab">
      <div className="border border-white/10 px-3 py-2 flex items-center gap-2">
        <Bank size={12} className="text-emerald-400" />
        <span className="mono text-[10px] uppercase tracking-widest text-zinc-400">Bank</span>
        <span className="flex-1 text-right text-[13px] font-bold mono tracking-wider">${(alliance.bank || 0).toLocaleString()}</span>
      </div>

      {/* Alliance-owned hospitals */}
      <div className="space-y-2">
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
          <span><Buildings size={11} className="inline mr-1" />Alliance-Owned Hospitals ({data.alliance.length})</span>
          {isStaff && (
            <button
              type="button"
              data-testid="place-alliance-hospital-btn"
              onClick={() => onOpenAllianceHospitalPlacement && onOpenAllianceHospitalPlacement()}
              className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 border border-pink-500/60 text-pink-300 hover:bg-pink-500/10"
            >
              <Plus size={9} className="inline mr-0.5" />Place ({data.alliance.length === 0 ? "FREE" : "$1M"})
            </button>
          )}
        </div>
        {loading && <div className="mono text-[10px] uppercase text-zinc-500 py-2 text-center">Loading…</div>}
        {!loading && data.alliance.length === 0 && (
          <div className="mono text-[10px] uppercase text-zinc-600 text-center py-3">— no alliance hospitals —</div>
        )}
        {data.alliance.map((h) => (
          <div key={h.id} className="border border-pink-500/30 p-2 space-y-1.5" data-testid={`alliance-hospital-${h.id}`}>
            <div className="flex items-center gap-2">
              <Buildings size={12} className="text-pink-400" />
              <span className="text-[12px] font-bold flex-1 truncate">{h.name}</span>
              <span className="mono text-[9px] uppercase tracking-widest text-zinc-500">{h.beds} beds</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {(h.specialties || []).map((s) => (
                <span key={s} className="mono text-[8px] uppercase tracking-widest px-1 py-px border border-emerald-400/40 text-emerald-300">{s}</span>
              ))}
              {h.specialties?.length === 0 && <span className="mono text-[8px] uppercase text-zinc-600">no specialties</span>}
            </div>
            {isStaff && (
              <div className="flex gap-1 pt-1 border-t border-white/5">
                {bedConfirmId === h.id ? (
                  <>
                    <button type="button" data-testid={`alliance-bed-confirm-${h.id}`} onClick={() => handleBuyBed(h.id)} className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-red-500/60 text-red-300 bg-red-500/10 hover:bg-red-500/20">Confirm Bed</button>
                    <button type="button" onClick={() => setBedConfirmId(null)} className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-white/10 text-zinc-400">Cancel</button>
                  </>
                ) : (
                  <button type="button" data-testid={`alliance-bed-${h.id}`} onClick={() => setBedConfirmId(h.id)} className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10">+ Bed</button>
                )}
                {["Trauma","Cath Lab","Stroke"].filter((s)=>!h.specialties?.includes(s)).map((s) => (
                  <button key={s} type="button" data-testid={`alliance-specialty-${h.id}-${s.toLowerCase().replace(/\s/g,'-')}`} onClick={() => handleBuySpecialty(h.id, s)} className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-400/40 text-amber-300 hover:bg-amber-400/10">+ {s} ($150k)</button>
                ))}
                {/* Demolish (Chief/Deputy only — gated by isStaff above) */}
                <span className="flex-1" />
                {deleteConfirmId === h.id ? (
                  <>
                    <button type="button" data-testid={`alliance-hospital-delete-confirm-${h.id}`} onClick={() => handleDelete(h.id)} className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-red-500/60 text-red-300 bg-red-500/10 hover:bg-red-500/20">Confirm Demolish</button>
                    <button type="button" onClick={() => setDeleteConfirmId(null)} className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-white/10 text-zinc-400">Cancel</button>
                  </>
                ) : (
                  <button type="button" data-testid={`alliance-hospital-delete-${h.id}`} onClick={() => setDeleteConfirmId(h.id)} title="Demolish alliance hospital (no refund)" className="mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-red-500/40 text-red-300 hover:bg-red-500/10">
                    <Trash size={9} className="inline" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Member-owned hospitals roll-up */}
      <div className="space-y-1 pt-2 border-t border-white/5">
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Member Hospitals ({data.members.length})</div>
        {data.members.length === 0 && (
          <div className="mono text-[10px] uppercase text-zinc-600 text-center py-2">— no member hospitals —</div>
        )}
        {data.members.map((h) => (
          <div key={h.id} className="border border-white/10 p-2 flex items-start gap-2" data-testid={`member-hospital-${h.id}`}>
            <Buildings size={12} className="text-cyan-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-bold truncate">{h.name}</div>
              <div className="mono text-[8px] uppercase tracking-widest text-zinc-500">
                {h.owner_name} · {h.beds} beds · fee {h.fee_pct}%
                {h.shared ? " · SHARED" : " · NOT SHARED"}
              </div>
              <div className="flex items-center gap-1 flex-wrap mt-1">
                {(h.specialties || []).map((s) => (
                  <span key={s} className="mono text-[8px] uppercase tracking-widest px-1 py-px border border-emerald-400/40 text-emerald-300">{s}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
